import { useEffect, useState } from 'react'
import CustomerAutocomplete from '../CustomerAutocomplete'
import { Customer, Product } from 'types'
import ProductAutocomplete from '../ProductAutocomplete'
import DatePicker from 'react-datepicker'
import { format } from 'date-fns'

import 'react-datepicker/dist/react-datepicker.css'
import { Button } from 'react-bootstrap'
import { useApi } from 'api'
import { useNavigate } from 'react-router-dom'
import ProductListComponent from './ProductList'

type QuantityProduct = {
  product: Product
  quantity: number
}

const DATE_FORMAT = 'yyyy-MM-dd'

export type ProductList = Map<Product['id'], QuantityProduct>

export default function InvoiceCreate() {
  const api = useApi()
  const navigate = useNavigate()
  const [isFormComplete, setFormComplete] = useState<boolean>(false)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [products, setProducts] = useState<ProductList>(
    new Map<Product['id'], QuantityProduct>()
  )
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(new Date())
  const [invoiceDeadline, setInvoiceDeadline] = useState<Date | null>(
    new Date()
  )

  function addProduct(p: Product | null) {
    if (!p) return
    if (products.has(p.id)) {
      return
    }
    products.set(p.id, { product: p, quantity: 1 })
    setProducts(new Map(products.entries()))
  }

  function formatDate(d: Date) {
    return format(d, 'yyyy-MM-dd')
  }

  async function createInvoice() {
    const invoicePayload = {
      customer_id: customer!.id,
      finalized: false,
      paid: false,
      date: formatDate(invoiceDate!),
      deadline: formatDate(invoiceDeadline!),
      invoice_lines_attributes: Array.from(products.entries()).map(
        ([id, { product, quantity }]) => {
          return { product_id: product.id, quantity }
        }
      ),
    }
    const response = await api.postInvoices(null, { invoice: invoicePayload })
    navigate(`/invoice/${response.data.id}`)
  }

  function navigateToHome() {
    navigate('/')
  }

  useEffect(
    function () {
      if (invoiceDate && invoiceDeadline && customer && products.size) {
        setFormComplete(true)
      } else {
        setFormComplete(false)
      }
    },
    [invoiceDate, invoiceDeadline, customer, products]
  )

  return (
    <>
      <div className="container">
        <div className="row mb-3 border-bottom">
          <h2>Create a new invoice</h2>
        </div>
        <div className="row">
          <div className="col-md-5">
            <h2>Client</h2>
            <div className="row">
              <CustomerAutocomplete
                value={customer}
                onChange={setCustomer}
              ></CustomerAutocomplete>
            </div>
          </div>
          <div className="col-md-2"></div>
          <div className="col-md-5">
            <h2>Dates</h2>
            <div className="row mb-3">
              <label htmlFor="date" className="col-sm-2 col-form-label">
                <strong>Date</strong>
              </label>
              <div className="col-sm-10">
                <DatePicker
                  id="date"
                  selected={invoiceDate}
                  onChange={setInvoiceDate}
                  dateFormat={DATE_FORMAT}
                ></DatePicker>
              </div>
            </div>
            <div className="row mn-3">
              <label htmlFor="date" className="col-sm-2 col-form-label">
                <strong>Deadline</strong>
              </label>
              <div className="col-sm-10">
                <DatePicker
                  id="deadline"
                  selected={invoiceDeadline}
                  onChange={setInvoiceDeadline}
                  dateFormat={DATE_FORMAT}
                ></DatePicker>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <h2>Products</h2>
          <div className="row">
            <ProductAutocomplete
              value={null}
              onChange={addProduct}
            ></ProductAutocomplete>
          </div>
          <div className="row">
            <ProductListComponent
              products={products}
              onChange={setProducts}
            ></ProductListComponent>
          </div>
          <div className="row mb-3">
            <Button disabled={!isFormComplete} onClick={createInvoice}>
              Create
            </Button>
          </div>
          <div className="row">
            <Button variant="outline-secondary" onClick={navigateToHome}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
